import { Box, IconButton, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useProgressTrackerSettingsContext } from './context/useProgressTrackerSettingsContext';
import React from 'react';
import { styles } from './ProgressTrackerSettings.styles';
import CircleIcon from '@mui/icons-material/Circle';
import { TimelineChartOrientation } from '@opengov/stories-shared/types';
import { HorizontalLabelPosition, VerticalLabelPosition } from './context/ProgressTracker.Provider';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
export type ProgressTrackerSettingItemProps = {
  id?: string;
  entityId?: number;
  status?: string;
  colorCode?: string;
  verticalLabelPosition?: VerticalLabelPosition;
  horizontalLabelPosition?: HorizontalLabelPosition;
};
export const ProgressTrackerSettingItem = (props: ProgressTrackerSettingItemProps) => {
  const MAX_STATUS_LENGTH = 15;
  const { updateLocalProgressTrackerSettings, setActiveSettingId, setShowColorModal, currentAlignment } =
    useProgressTrackerSettingsContext();
  const { status, colorCode, verticalLabelPosition, horizontalLabelPosition } = props;
  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateLocalProgressTrackerSettings({ ...props, status: e.target.value });
  };

  const onClickColorBox = () => {
    setActiveSettingId(props.id || '');
    setShowColorModal(true);
  };
  const handleLabelPositioningChange = (e: React.MouseEvent<HTMLElement>, newValue: VerticalLabelPosition | HorizontalLabelPosition) => {
    if (newValue && currentAlignment === TimelineChartOrientation.HORIZONTAL) {
      updateLocalProgressTrackerSettings({ ...props, horizontalLabelPosition: newValue as HorizontalLabelPosition });
    } else if (newValue && currentAlignment === TimelineChartOrientation.VERTICAL) {
      updateLocalProgressTrackerSettings({ ...props, verticalLabelPosition: newValue as VerticalLabelPosition });
    }
  };
  return (
    <Box sx={styles.progressTrackerSettingItem}>
      <Box sx={styles.statusColorContainer}>
        <IconButton onClick={onClickColorBox} aria-label={`Edit color for ${status || 'status'}`} sx={styles.colorBox}>
          <CircleIcon aria-hidden={true} sx={{ color: colorCode }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <TextField
            size="small"
            variant="outlined"
            value={status || ''}
            onChange={handleStatusChange}
            error={status === '' || status === undefined}
            inputProps={{ maxLength: MAX_STATUS_LENGTH, className: 'no-format', 'aria-label': 'Status' }}
          />
        </Box>
      </Box>
      <Box sx={styles.labelPositioningContainer}>
        <Box sx={styles.labelPositioningToggleButtonGroupContainer}>
          <ToggleButtonGroup
            sx={styles.labelPositioningToggleButtonGroup}
            color="primary"
            exclusive
            aria-label="Horizontal label position"
            value={horizontalLabelPosition}
            onChange={(e: React.MouseEvent<HTMLElement>, newValue: HorizontalLabelPosition | VerticalLabelPosition) => {
              handleLabelPositioningChange(e, newValue);
            }}
            disabled={currentAlignment === TimelineChartOrientation.VERTICAL}
          >
            <ToggleButton sx={styles.labelPositioningToggleButton} value={HorizontalLabelPosition.Top}
              selected={horizontalLabelPosition === HorizontalLabelPosition.Top}
              aria-label="Label above"
            >
              <ArrowUpwardIcon aria-hidden={true} />
            </ToggleButton>
            <ToggleButton sx={styles.labelPositioningToggleButton} value={HorizontalLabelPosition.Bottom}
              selected={horizontalLabelPosition === HorizontalLabelPosition.Bottom}
              aria-label="Label below"
            >
              <ArrowDownwardIcon aria-hidden={true} />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup color="primary" exclusive aria-label="Vertical label position"
            value={verticalLabelPosition}
            onChange={(e: React.MouseEvent<HTMLElement>, newValue: VerticalLabelPosition | HorizontalLabelPosition) => {
              handleLabelPositioningChange(e, newValue);
            }}
            disabled={currentAlignment === TimelineChartOrientation.HORIZONTAL}
          >
            <ToggleButton sx={styles.labelPositioningToggleButton} value={VerticalLabelPosition.Left}
              selected={verticalLabelPosition === VerticalLabelPosition.Left}
              aria-label="Label left"
            >
              <ArrowBackIcon aria-hidden={true} />
            </ToggleButton>
            <ToggleButton sx={styles.labelPositioningToggleButton} value={VerticalLabelPosition.Right}
              selected={verticalLabelPosition === VerticalLabelPosition.Right}
              aria-label="Label right"
            >
              <ArrowForwardIcon aria-hidden={true} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
    </Box>
  );
};
